import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import {
    Box, Typography, Grid, Button, Stack, Chip, Avatar, IconButton, Tooltip, InputBase, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, InputAdornment, Snackbar, Alert,
    Menu, ListItemIcon,
} from '@mui/material';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import MailRoundedIcon from '@mui/icons-material/MailRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { inr, initialsFromName as initials, avatarTone } from '../../utils/format';
import { solidBtn, ghostBtn, tonalBtn, field, th, td, Panel, EmptyState, ConfirmDialog, SkeletonRows, LoadAlert } from '../uiKit';
import http, { apiErrorMessage } from '../../Api/http';
import {
    GetCandidates, AddCandidate, UpdateCandidate, SetCandidateStage, DeleteCandidate, GetVacancies,
    AddCandidateCommunication, UpdateCandidateCommunication, DeleteCandidateCommunication,
} from '../../Api/Api';

const SOURCES = ['Referral', 'LinkedIn', 'Naukri', 'Walk-in', 'Career Page', 'Agency', 'Other'];
const NOTICE = ['Immediate', '15 days', '30 days', '60 days', '90 days'];

const STAGES = {
    applied: { label: 'Applied', color: '#64748B', bg: '#F1F5F9' },
    interviewing: { label: 'Interviewing', color: '#0369A1', bg: '#E0F2FE' },
    'on-hold': { label: 'On Hold', color: '#B45309', bg: '#FFF7ED' },
    selected: { label: 'Selected', color: '#16A34A', bg: '#DCFCE7' },
    joined: { label: 'Joined', color: '#15803D', bg: '#DCFCE7' },
    rejected: { label: 'Rejected', color: '#E11D48', bg: '#FEE2E2' },
};
const stageKey = (s) => {
    const t = String(s || '').toLowerCase().replace(/\s+/g, '-');
    return STAGES[t] ? t : 'applied';
};
const STAGE_API = {
    applied: 'Applied', interviewing: 'Interviewing', 'on-hold': 'On Hold',
    selected: 'Selected', joined: 'Joined', rejected: 'Rejected',
};
const STAGE_ORDER = ['applied', 'interviewing', 'on-hold', 'selected', 'joined', 'rejected'];

const EMPTY = {
    vacancyId: '', name: '', email: '', phone: '', experience: '', currentCompany: '',
    expectedSalary: '', noticePeriod: '30 days', source: 'Referral', communications: [],
};

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const appliedISO = (s) => {
    const m = /(\d{1,2})\s+([A-Za-z]{3})\w*\s+(\d{4})/.exec(s || '');
    const i = m ? MONTHS.indexOf(m[2].toLowerCase()) : -1;
    return i < 0 ? '' : `${m[3]}-${String(i + 1).padStart(2, '0')}-${m[1].padStart(2, '0')}`;
};
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dateRangeFor = (mode) => {
    const now = new Date();
    if (mode === 'today') return [isoOf(now), isoOf(now)];
    if (mode === 'this') return [isoOf(new Date(now.getFullYear(), now.getMonth(), 1)), isoOf(new Date(now.getFullYear(), now.getMonth() + 1, 0))];
    if (mode === 'last') return [isoOf(new Date(now.getFullYear(), now.getMonth() - 1, 1)), isoOf(new Date(now.getFullYear(), now.getMonth(), 0))];
    return ['', ''];
};

const FOLLOW_UP_STATUS = 'Follow-up Scheduled';
const MAX_COMM_ROWS = 10;

const normalizeCommunication = (c) => ({
    id: c.id,
    candidateId: c.candidateId,
    communicationType: c.communicationType ?? '',
    status: c.status ?? '',
    description: c.description ?? '',
    communicatedOn: c.communicatedOn ?? '',
    followUpDate: c.followUpDate ?? null,
    loggedBy: c.loggedBy ?? '',
    loggedOn: c.loggedOn ?? '',
});

const normalizeCandidate = (c) => ({
    id: c.id,
    vacancyId: c.vacancyId,
    vacancyTitle: c.vacancyTitle ?? '',
    name: c.fullName ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    currentCompany: c.currentCompany ?? '',
    isFresher: Boolean(c.isFresher),
    experienceYears: Number(c.experienceYears) || 0,
    expectedSalary: Number(c.expectedSalary) || 0,
    noticePeriod: c.noticePeriod ?? '',
    source: c.source ?? '',
    stage: stageKey(c.stage),
    appliedOn: c.appliedOn ?? '',
    rejectReason: c.rejectReason ?? null,
    roundsDecided: Number(c.roundsDecided) || 0,
    roundsTotal: Number(c.roundsTotal) || 0,
    communications: Array.isArray(c.communications) ? c.communications.map(normalizeCommunication) : [],
});

const expText = (c) => (c.isFresher || c.experienceYears === 0 ? 'Fresher' : `${c.experienceYears} yr${c.experienceYears === 1 ? '' : 's'}`);

// dd-MM-yyyy the API wants, from a <input type="date"> value (yyyy-mm-dd).
const toDMY = (iso) => {
    if (!iso) return '';
    const [y, m, d] = String(iso).slice(0, 10).split('-');
    return d ? `${d}-${m}-${y}` : '';
};
// The log renders dates pretty ("15 Sep 2026"); parse that back to yyyy-mm-dd
// to prefill a date input when editing an entry.
const prettyToISO = (s) => {
    if (!s) return '';
    const t = new Date(s);
    if (Number.isNaN(t.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
};

const emptyCommRow = () => ({ communicationType: '', status: '', description: '', communicatedOn: '', followUpDate: '' });
const commRowFilled = (row) => Boolean(row.communicationType || row.status || row.description.trim());
const commRowValid = (row) => row.communicationType && row.status && (row.status !== FOLLOW_UP_STATUS || row.followUpDate);
const commPayload = (row) => {
    const out = {
        communicationType: row.communicationType,
        status: row.status,
        description: row.description.trim(),
    };
    if (row.communicatedOn) out.communicatedOn = toDMY(row.communicatedOn);
    if (row.status === FOLLOW_UP_STATUS && row.followUpDate) out.followUpDate = toDMY(row.followUpDate);
    return out;
};

export default function CandidatesTab({ setActions }) {
    const [candidates, setCandidates] = useState([]);
    const [openVacancies, setOpenVacancies] = useState([]);
    const [commMeta, setCommMeta] = useState({ types: [], statuses: [] });
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);

    const [q, setQ] = useState('');
    const [stageFilter, setStageFilter] = useState('all');
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [tried, setTried] = useState(false);
    const [snack, setSnack] = useState(null);
    const [logFor, setLogFor] = useState(null);

    const [stageAnchor, setStageAnchor] = useState(null);
    const [stageTarget, setStageTarget] = useState(null);
    const [reject, setReject] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [confirm, setConfirm] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const loadCandidates = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetCandidates);
            if (body?.error) throw new Error(body.message || 'Could not load candidates.');
            const list = Array.isArray(body?.data?.candidates) ? body.data.candidates : [];
            setCandidates(list.map(normalizeCandidate));
            setCommMeta({
                types: Array.isArray(body?.data?.communicationTypes) ? body.data.communicationTypes : [],
                statuses: Array.isArray(body?.data?.communicationStatuses) ? body.data.communicationStatuses : [],
            });
            setLoadError('');
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load candidates.'));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadVacancies = useCallback(async () => {
        try {
            const { data: body } = await http.get(GetVacancies);
            if (body?.error) return;
            const list = Array.isArray(body?.data?.vacancies) ? body.data.vacancies : [];
            setOpenVacancies(list
                .filter((v) => v.status === 'Open')
                .map((v) => ({ id: v.id, title: v.roleTitle, department: v.department, open: Math.max(0, (Number(v.openings) || 0) - (Number(v.filled) || 0)) })));
        } catch { }
    }, []);

    useEffect(() => { loadCandidates(); loadVacancies(); }, [loadCandidates, loadVacancies]);

    // Keep an open communication-log dialog in sync with a fresh reload.
    useEffect(() => {
        if (!logFor) return;
        const fresh = candidates.find((c) => c.id === logFor.id);
        if (fresh && fresh !== logFor) setLogFor(fresh);
    }, [candidates, logFor]);

    const [vacancyFilter, setVacancyFilter] = useState('all');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [dateMode, setDateMode] = useState('all');
    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        const [rangeFrom, rangeTo] = dateRangeFor(dateMode);
        return candidates.filter((c) => {
            if (stageFilter !== 'all' && c.stage !== stageFilter) return false;
            if (vacancyFilter !== 'all' && String(c.vacancyId) !== vacancyFilter) return false;
            if (sourceFilter !== 'all' && c.source !== sourceFilter) return false;
            if (rangeFrom || rangeTo) {
                const d = appliedISO(c.appliedOn);
                if (!d || d < rangeFrom || d > rangeTo) return false;
            }
            if (!s) return true;
            return [c.name, c.email, c.phone, c.currentCompany, c.vacancyTitle].some((x) => String(x || '').toLowerCase().includes(s));
        });
    }, [candidates, q, stageFilter, vacancyFilter, sourceFilter, dateMode]);
    const filtersOn = !!q || stageFilter !== 'all' || vacancyFilter !== 'all' || sourceFilter !== 'all' || dateMode !== 'all';
    const sourceChoices = useMemo(() => [...new Set(candidates.map((c) => c.source).filter(Boolean))].sort(), [candidates]);
    // Only vacancies that actually have candidates are offered, so a pick never returns an empty list.
    const vacancyChoices = useMemo(() => {
        const seen = new Map();
        candidates.forEach((c) => { if (c.vacancyId != null && !seen.has(String(c.vacancyId))) seen.set(String(c.vacancyId), c.vacancyTitle || 'Unknown role'); });
        return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [candidates]);

    const openCreate = () => { setForm(EMPTY); setTried(false); setDialog({ mode: 'create' }); };
    const openEdit = (c) => {
        setForm({
            vacancyId: c.vacancyId,
            name: c.name,
            email: c.email,
            phone: c.phone,
            experience: c.experienceYears ? String(c.experienceYears) : '',
            currentCompany: c.currentCompany,
            expectedSalary: c.expectedSalary ? String(c.expectedSalary) : '',
            noticePeriod: c.noticePeriod || '30 days',
            source: c.source || 'Referral',
            communications: [],
        });
        setTried(false);
        setDialog({ mode: 'edit', id: c.id, vacancyTitle: c.vacancyTitle });
    };
    const closeDialog = () => { if (!saving) setDialog(null); };
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const setCommRow = (i, key) => (e) => setForm((f) => ({
        ...f, communications: f.communications.map((row, idx) => (idx === i ? { ...row, [key]: e.target.value } : row)),
    }));
    const addCommRow = () => setForm((f) => (f.communications.length >= MAX_COMM_ROWS ? f : { ...f, communications: [...f.communications, emptyCommRow()] }));
    const removeCommRow = (i) => setForm((f) => ({ ...f, communications: f.communications.filter((_, idx) => idx !== i) }));

    const vacancyOptions = useMemo(() => {
        if (dialog?.mode === 'edit' && form.vacancyId && !openVacancies.some((v) => v.id === form.vacancyId)) {
            return [{ id: form.vacancyId, title: dialog.vacancyTitle || 'Current vacancy', department: '', open: 0, closed: true }, ...openVacancies];
        }
        return openVacancies;
    }, [dialog, form.vacancyId, openVacancies]);

    const filledCommRows = form.communications.filter(commRowFilled);
    const invalidCommRows = filledCommRows.some((row) => !commRowValid(row));
    const valid = form.name.trim() && form.vacancyId && !invalidCommRows;

    const submit = async () => {
        setTried(true);
        if (!valid) {
            notify(!form.name.trim() ? 'The candidate needs a name.'
                : !form.vacancyId ? 'Pick the vacancy they are applying for.'
                    : 'Every communication row needs a type, a status, and a follow-up date if one is scheduled.', 'warning');
            return;
        }

        const payload = {
            vacancyId: form.vacancyId,
            fullName: form.name.trim(),
            email: form.email || '',
            phone: form.phone || '',
            currentCompany: form.currentCompany || '',
            experience: Number(form.experience) || 0,
            expectedSalary: Number(form.expectedSalary) || 0,
            noticePeriod: form.noticePeriod,
            source: form.source,
        };
        const editing = dialog?.mode === 'edit';
        if (!editing && filledCommRows.length > 0) {
            payload.communications = filledCommRows.map(commPayload);
        }

        setSaving(true);
        try {
            const { data: body } = editing
                ? await http.put(UpdateCandidate, { id: dialog.id, ...payload })
                : await http.post(AddCandidate, payload);
            if (body?.error) throw new Error(body.message || 'Could not save the candidate.');
            const vacTitle = vacancyOptions.find((v) => v.id === form.vacancyId)?.title || 'the vacancy';
            notify(body?.message || (editing ? `${payload.fullName} updated.` : `${payload.fullName} added to ${vacTitle}.`));
            setDialog(null);
            await loadCandidates();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the candidate.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const setStage = async (candidate, key, reason = '') => {
        if (busyId) return;
        setBusyId(candidate.id);
        try {
            const { data: body } = await http.put(SetCandidateStage, {
                id: candidate.id,
                stage: STAGE_API[key],
                rejectReason: key === 'rejected' ? reason.trim() : '',
            });
            if (body?.error) throw new Error(body.message || 'Could not update the stage.');
            notify(body?.message || `${candidate.name} moved to ${STAGES[key].label}.`);
            setReject(null);
            setRejectReason('');
            await loadCandidates();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not update the stage.'), 'error');
        } finally {
            setBusyId(null);
        }
    };

    const pickStage = (key) => {
        const candidate = stageTarget;
        setStageAnchor(null);
        setStageTarget(null);
        if (!candidate || key === candidate.stage) return;
        if (key === 'rejected') { setRejectReason(''); setReject(candidate); return; }
        setStage(candidate, key);
    };

    const doDelete = async () => {
        if (!confirm || deleting) return;
        setDeleting(true);
        try {
            const { data: body } = await http.delete(DeleteCandidate, { params: { id: confirm.id } });
            if (body?.error) throw new Error(body.message || 'Could not remove the candidate.');
            notify(body?.message || `${confirm.name} removed.`);
            setConfirm(null);
            await loadCandidates();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not remove the candidate.'), 'error');
        } finally {
            setDeleting(false);
        }
    };

    // The search box and primary button sit at the right end of the breadcrumb row.
    const actionsRef = useRef({});
    actionsRef.current = { openCreate };
    useEffect(() => {
        if (!setActions) return undefined;
        setActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', px: 1.4, height: 42, width: { xs: '100%', sm: 280 } }}>
                        <SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} />
                        <InputBase placeholder="Search candidates…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                    </Stack>
                    <Button startIcon={<AddRoundedIcon />} onClick={() => actionsRef.current.openCreate()} sx={{ ...solidBtn, height: 42, px: 2.2 }}>Add Candidate</Button>
            </Box>,
        );
        return () => setActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setActions, q]);

    return (
        <Box sx={{ p: 2, pt: 1.5 }}>
            <Panel
                title="Candidates"
                icon={PeopleAltRoundedIcon}
                chip={`${filtered.length} of ${candidates.length}`}
                action={(
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <TextField
                            select size="small" value={vacancyFilter} onChange={(e) => setVacancyFilter(e.target.value)}
                            sx={{ ...field, width: 170, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                        >
                            <MenuItem value="all" sx={{ fontSize: 13 }}>All vacancies</MenuItem>
                            {vacancyChoices.map(([id, t]) => <MenuItem key={id} value={id} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                        </TextField>
                        <TextField
                            select size="small" value={dateMode} onChange={(e) => setDateMode(e.target.value)}
                            sx={{ ...field, width: 140, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                        >
                            <MenuItem value="all" sx={{ fontSize: 13 }}>All dates</MenuItem>
                            <MenuItem value="today" sx={{ fontSize: 13 }}>Today</MenuItem>
                            <MenuItem value="this" sx={{ fontSize: 13 }}>This month</MenuItem>
                            <MenuItem value="last" sx={{ fontSize: 13 }}>Last month</MenuItem>
                        </TextField>
                        <TextField
                            select size="small" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                            sx={{ ...field, width: 140, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                        >
                            <MenuItem value="all" sx={{ fontSize: 13 }}>All sources</MenuItem>
                            {sourceChoices.map((x) => <MenuItem key={x} value={x} sx={{ fontSize: 13 }}>{x}</MenuItem>)}
                        </TextField>
                        <TextField
                            select size="small" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                            sx={{ ...field, width: 150, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                        >
                            <MenuItem value="all" sx={{ fontSize: 13 }}>All stages</MenuItem>
                            {Object.entries(STAGES).map(([k, s]) => <MenuItem key={k} value={k} sx={{ fontSize: 13 }}>{s.label}</MenuItem>)}
                        </TextField>
                        <Tooltip arrow title="Reload">
                            <IconButton onClick={() => { loadCandidates(); loadVacancies(); }} disabled={loading} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', color: '#64748B', height: 38, width: 38, '&:hover': { bgcolor: '#E4F0F9', color: '#135F93' } }}>
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            >
                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <Box sx={{ p: 3 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }}
                            action={<Button size="small" onClick={loadCandidates} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                            {loadError}
                        </LoadAlert>
                    </Box>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        icon={PeopleAltRoundedIcon}
                        title={filtersOn ? 'No candidates match these filters' : 'No candidates yet'}
                        hint={filtersOn ? 'Clear the search or change the filters.' : 'Add an applicant against an open vacancy, then schedule their first round.'}
                    />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', minWidth: 940, borderCollapse: 'collapse' }}>
                            <Box component="thead" sx={{ bgcolor: '#F3F8FC' }}>
                                <Box component="tr">
                                    {['CANDIDATE', 'APPLIED FOR', 'EXPERIENCE', 'EXPECTED', 'ROUNDS', 'STAGE', 'ACTIONS'].map((h) => (
                                        <Box component="th" key={h} sx={{ ...th, textAlign: h === 'ACTIONS' ? 'right' : 'left' }}>{h}</Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {filtered.map((c, i) => {
                                    const stage = STAGES[c.stage] || STAGES.applied;
                                    return (
                                        <Box component="tr" key={c.id} sx={{ bgcolor: i % 2 ? '#F3F8FC' : '#fff' }}>
                                            <Box component="td" sx={td}>
                                                <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center' }}>
                                                    <Avatar sx={{ width: 38, height: 38, ...avatarTone(c.name), fontSize: 13, fontWeight: 700 }}>{initials(c.name)}</Avatar>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{c.name}</Typography>
                                                        <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{c.email || c.phone || '—'}</Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                            <Box component="td" sx={td}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{c.vacancyTitle || 'Unknown role'}</Typography>
                                                <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{c.source}{c.appliedOn ? ` · applied ${c.appliedOn}` : ''}</Typography>
                                            </Box>
                                            <Box component="td" sx={td}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{expText(c)}</Typography>
                                                <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{c.currentCompany || 'Fresher'}</Typography>
                                            </Box>
                                            <Box component="td" sx={td}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{c.expectedSalary ? inr(c.expectedSalary) : '—'}</Typography>
                                                <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{c.noticePeriod || '—'} notice</Typography>
                                            </Box>
                                            <Box component="td" sx={td}>
                                                {c.roundsTotal === 0
                                                    ? <Typography sx={{ fontSize: 12, color: '#C4C9D4', fontStyle: 'italic' }}>Not scheduled</Typography>
                                                    : <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{c.roundsDecided} of {c.roundsTotal} decided</Typography>}
                                            </Box>
                                            <Box component="td" sx={td}>
                                                <Tooltip arrow title="Change stage">
                                                    <Chip
                                                        label={stage.label} size="small" clickable
                                                        disabled={busyId === c.id}
                                                        onClick={(e) => { setStageTarget(c); setStageAnchor(e.currentTarget); }}
                                                        onDelete={(e) => { setStageTarget(c); setStageAnchor(e.currentTarget); }}
                                                        deleteIcon={busyId === c.id ? <CircularProgress size={12} sx={{ color: stage.color }} /> : <KeyboardArrowDownRoundedIcon sx={{ fontSize: 15 }} />}
                                                        sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: stage.bg, color: stage.color, '& .MuiChip-deleteIcon': { color: stage.color, '&:hover': { color: stage.color } } }}
                                                    />
                                                </Tooltip>
                                                {c.stage === 'rejected' && c.rejectReason && (
                                                    <Tooltip arrow title={c.rejectReason}>
                                                        <Typography sx={{ fontSize: 10.5, color: '#E11D48', mt: 0.3, maxWidth: 150, cursor: 'help' }} noWrap>{c.rejectReason}</Typography>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Box component="td" sx={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <Tooltip arrow title="Communication log">
                                                    <IconButton size="small" onClick={() => setLogFor(c)} sx={{ color: c.communications.length ? '#135F93' : '#94A3B8', '&:hover': { color: '#135F93', bgcolor: '#E4F0F9' } }}>
                                                        {c.communications.length > 0 ? (
                                                            <Chip label={c.communications.length} size="small" icon={<ChatBubbleOutlineRoundedIcon sx={{ fontSize: '13px !important' }} />}
                                                                sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#E4F0F9', color: '#135F93', '& .MuiChip-icon': { color: 'inherit', ml: '5px' }, '& .MuiChip-label': { px: '5px' } }} />
                                                        ) : <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 17 }} />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip arrow title="Edit">
                                                    <IconButton size="small" onClick={() => openEdit(c)} sx={{ ml: 0.4, color: '#94A3B8', '&:hover': { color: '#135F93', bgcolor: '#E4F0F9' } }}><EditRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
                                                </Tooltip>
                                                <Tooltip arrow title="Remove">
                                                    <IconButton size="small" onClick={() => setConfirm(c)} sx={{ ml: 0.4, color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}><DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Panel>

            <Dialog open={Boolean(dialog)} onClose={closeDialog} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{dialog?.mode === 'edit' ? 'Edit Candidate' : 'Add Candidate'}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>
                        {dialog?.mode === 'edit' ? 'Update the applicant’s details. Their stage is changed from the table.' : 'They start as Applied, and move to Interviewing once you schedule their first round.'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Grid container spacing={1.8}>
                        <Grid size={12}>
                            <TextField select label="Applying for" size="small" fullWidth value={form.vacancyId} onChange={set('vacancyId')}
                                error={tried && !form.vacancyId} sx={field}
                                helperText={vacancyOptions.length === 0 ? 'No open vacancies — open one on the Vacancies tab first.' : ' '}>
                                {vacancyOptions.map((v) => (
                                    <MenuItem key={v.id} value={v.id} sx={{ fontSize: 13.5 }}>
                                        {v.title}{v.department ? ` — ${v.department}` : ''}{v.closed ? ' (closed)' : ` (${v.open} open)`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Full name" size="small" fullWidth value={form.name} onChange={set('name')} error={tried && !form.name.trim()} sx={field} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Email" size="small" fullWidth value={form.email} onChange={set('email')}
                                slotProps={{ input: { startAdornment: <InputAdornment position="start"><MailRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment> } }} sx={field} />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Phone" size="small" fullWidth value={form.phone} onChange={set('phone')}
                                slotProps={{ input: { startAdornment: <InputAdornment position="start"><CallRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment> } }} sx={field} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Current company" size="small" fullWidth value={form.currentCompany} onChange={set('currentCompany')}
                                placeholder="Leave blank for a fresher"
                                slotProps={{ input: { startAdornment: <InputAdornment position="start"><BusinessRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment> } }} sx={field} />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Experience (years)" type="number" size="small" fullWidth value={form.experience} onChange={set('experience')} placeholder="e.g. 5" slotProps={{ htmlInput: { min: 0, step: 0.5 } }} sx={field} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField label="Expected salary" type="number" size="small" fullWidth value={form.expectedSalary} onChange={set('expectedSalary')}
                                slotProps={{ input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> } }} sx={field} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField select label="Notice period" size="small" fullWidth value={form.noticePeriod} onChange={set('noticePeriod')} sx={field}>
                                {NOTICE.map((n) => <MenuItem key={n} value={n} sx={{ fontSize: 13.5 }}>{n}</MenuItem>)}
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField select label="Source" size="small" fullWidth value={form.source} onChange={set('source')} sx={field}>
                                {SOURCES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 13.5 }}>{s}</MenuItem>)}
                            </TextField>
                        </Grid>

                        {dialog?.mode === 'create' && (
                            <Grid size={12}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.8, mt: 0.6 }}>
                                    Log a communication (optional)
                                </Typography>
                                <Stack spacing={1.2}>
                                    {form.communications.map((row, i) => (
                                        <Box key={i} sx={{ p: 1.4, borderRadius: '9px', border: '1px solid #EEF0F6', bgcolor: '#FAFAFD' }}>
                                            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Chip label={`Entry ${i + 1}`} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#E4F0F9', color: '#135F93' }} />
                                                <IconButton size="small" onClick={() => removeCommRow(i)} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Stack>
                                            <Grid container spacing={1.2}>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField
                                                        select label="Type" size="small" fullWidth value={row.communicationType}
                                                        onChange={setCommRow(i, 'communicationType')}
                                                        error={tried && commRowFilled(row) && !row.communicationType} sx={field}
                                                    >
                                                        {commMeta.types.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                                                    </TextField>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField
                                                        select label="Status" size="small" fullWidth value={row.status}
                                                        onChange={setCommRow(i, 'status')}
                                                        error={tried && commRowFilled(row) && !row.status} sx={field}
                                                    >
                                                        {commMeta.statuses.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
                                                    </TextField>
                                                </Grid>
                                                <Grid size={12}>
                                                    <TextField
                                                        label="Description" size="small" fullWidth multiline minRows={2}
                                                        value={row.description} onChange={setCommRow(i, 'description')} sx={field}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: row.status === FOLLOW_UP_STATUS ? 6 : 12 }}>
                                                    <TextField
                                                        label="Communicated on (optional)" type="date" size="small" fullWidth
                                                        value={row.communicatedOn} onChange={setCommRow(i, 'communicatedOn')}
                                                        sx={field} slotProps={{ inputLabel: { shrink: true } }}
                                                    />
                                                </Grid>
                                                {row.status === FOLLOW_UP_STATUS && (
                                                    <Grid size={{ xs: 12, sm: 6 }}>
                                                        <TextField
                                                            label="Follow-up date" type="date" size="small" fullWidth
                                                            value={row.followUpDate} onChange={setCommRow(i, 'followUpDate')}
                                                            error={tried && !row.followUpDate} sx={field}
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Box>
                                    ))}
                                </Stack>
                                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mt: 1.2 }}>
                                    <Button
                                        onClick={addCommRow} disabled={form.communications.length >= MAX_COMM_ROWS}
                                        startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />} sx={{ ...tonalBtn, height: 34, fontSize: 12.5 }}
                                    >
                                        Add communication
                                    </Button>
                                    {form.communications.length > 0 && (
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#98A0AE' }}>{form.communications.length} / {MAX_COMM_ROWS}</Typography>
                                    )}
                                </Stack>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeDialog} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                        {saving ? 'Saving…' : dialog?.mode === 'edit' ? 'Save Changes' : 'Add Candidate'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu anchorEl={stageAnchor} open={Boolean(stageAnchor)} onClose={() => { setStageAnchor(null); setStageTarget(null); }}
                slotProps={{ paper: { sx: { borderRadius: '9px', minWidth: 190 } } }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#9CA3AF', px: 1.5, py: 0.7, letterSpacing: 0.4 }}>MOVE TO STAGE</Typography>
                {STAGE_ORDER.map((key) => {
                    const s = STAGES[key];
                    const isCurrent = stageTarget?.stage === key;
                    return (
                        <MenuItem key={key} onClick={() => pickStage(key)} disabled={Boolean(busyId)} sx={{ fontSize: 13, gap: 1 }}>
                            <ListItemIcon sx={{ minWidth: 0 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
                            </ListItemIcon>
                            {s.label}
                            {isCurrent && <CheckRoundedIcon sx={{ fontSize: 16, color: s.color, ml: 'auto' }} />}
                        </MenuItem>
                    );
                })}
            </Menu>

            <Dialog open={Boolean(reject)} onClose={() => { if (!busyId) setReject(null); }} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 0.5 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>Reject candidate</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>{reject?.name}{reject?.vacancyTitle ? ` · ${reject.vacancyTitle}` : ''}</Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <TextField label="Reason" size="small" fullWidth multiline minRows={3} autoFocus
                        placeholder="e.g. Salary expectations above band; strong profile — keep for future roles."
                        value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} sx={field} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setReject(null)} disabled={Boolean(busyId)} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={() => setStage(reject, 'rejected', rejectReason)} disabled={Boolean(busyId) || !rejectReason.trim()}
                        startIcon={busyId ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                        sx={{ height: 40, px: 2.4, fontWeight: 700, textTransform: 'none', borderRadius: '7px', bgcolor: '#DC2626', color: '#fff', '&:hover': { bgcolor: '#B91C1C' }, '&.Mui-disabled': { bgcolor: '#FCA5A5', color: '#fff' } }}>
                        {busyId ? 'Rejecting…' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirm)}
                onClose={() => { if (!deleting) setConfirm(null); }}
                onConfirm={doDelete}
                title="Remove this candidate?"
                body={confirm ? `${confirm.name} and their interview history for ${confirm.vacancyTitle || 'this vacancy'} will be removed. This cannot be undone.` : ''}
                confirmLabel="Remove"
            />

            <CommunicationLogDialog
                candidate={logFor}
                types={commMeta.types}
                statuses={commMeta.statuses}
                onClose={() => setLogFor(null)}
                notify={notify}
                onChanged={loadCandidates}
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={3400} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}

// A per-candidate log, opened from the table row: view every past entry and
// add/edit/delete one at a time, independent of the candidate's own save.
function CommunicationLogDialog({ candidate, types, statuses, onClose, notify, onChanged }) {
    const [form, setForm] = useState(null); // row being added/edited, or null when just browsing
    const [editingId, setEditingId] = useState(null);
    const [tried, setTried] = useState(false);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const close = () => { if (!saving && !busyId) { setForm(null); setEditingId(null); onClose(); } };

    const startAdd = () => { setForm(emptyCommRow()); setEditingId(null); setTried(false); };
    const startEdit = (entry) => {
        setForm({
            communicationType: entry.communicationType || '',
            status: entry.status || '',
            description: entry.description || '',
            communicatedOn: '',
            followUpDate: prettyToISO(entry.followUpDate),
        });
        setEditingId(entry.id);
        setTried(false);
    };
    const cancelForm = () => { setForm(null); setEditingId(null); setTried(false); };

    const saveForm = async () => {
        setTried(true);
        if (!commRowValid(form)) return;
        setSaving(true);
        try {
            const { data: body } = editingId
                ? await http.put(UpdateCandidateCommunication, { id: editingId, ...commPayload(form) })
                : await http.post(AddCandidateCommunication, { candidateId: candidate.id, ...commPayload(form) });
            if (body?.error) throw new Error(body.message || 'Could not save the communication.');
            notify(editingId ? 'Communication updated.' : 'Communication logged.');
            setForm(null);
            setEditingId(null);
            await onChanged();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the communication.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async () => {
        if (!deleteTarget) return;
        setBusyId(deleteTarget.id);
        try {
            const { data: body } = await http.delete(DeleteCandidateCommunication, { params: { id: deleteTarget.id } });
            if (body?.error) throw new Error(body.message || 'Could not remove the communication.');
            notify('Communication removed.');
            setDeleteTarget(null);
            await onChanged();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not remove the communication.'), 'error');
        } finally {
            setBusyId(null);
        }
    };

    if (!candidate) return null;
    const list = candidate.communications || [];

    return (
        <Dialog open={Boolean(candidate)} onClose={close} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Communication Log</Typography>
                <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>{candidate.name}{candidate.vacancyTitle ? ` · ${candidate.vacancyTitle}` : ''}</Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                {form ? (
                    <Box sx={{ p: 1.4, borderRadius: '9px', border: '1px solid #EEF0F6', bgcolor: '#FAFAFD', mb: 2 }}>
                        <Grid container spacing={1.2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField select label="Type" size="small" fullWidth value={form.communicationType}
                                    onChange={(e) => setForm((f) => ({ ...f, communicationType: e.target.value }))}
                                    error={tried && !form.communicationType} sx={field}>
                                    {types.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField select label="Status" size="small" fullWidth value={form.status}
                                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                                    error={tried && !form.status} sx={field}>
                                    {statuses.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid size={12}>
                                <TextField label="Description" size="small" fullWidth multiline minRows={2}
                                    value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} sx={field} />
                            </Grid>
                            {!editingId && (
                                <Grid size={{ xs: 12, sm: form.status === FOLLOW_UP_STATUS ? 6 : 12 }}>
                                    <TextField label="Communicated on (optional)" type="date" size="small" fullWidth
                                        value={form.communicatedOn} onChange={(e) => setForm((f) => ({ ...f, communicatedOn: e.target.value }))}
                                        sx={field} slotProps={{ inputLabel: { shrink: true } }} />
                                </Grid>
                            )}
                            {form.status === FOLLOW_UP_STATUS && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField label="Follow-up date" type="date" size="small" fullWidth
                                        value={form.followUpDate} onChange={(e) => setForm((f) => ({ ...f, followUpDate: e.target.value }))}
                                        error={tried && !form.followUpDate} sx={field} slotProps={{ inputLabel: { shrink: true } }} />
                                </Grid>
                            )}
                        </Grid>
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 1.4 }}>
                            <Button onClick={cancelForm} disabled={saving} sx={{ ...ghostBtn, height: 34, fontSize: 12.5 }}>Cancel</Button>
                            <Button onClick={saveForm} disabled={saving} startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 34, fontSize: 12.5 }}>
                                {saving ? 'Saving…' : editingId ? 'Save entry' : 'Log entry'}
                            </Button>
                        </Stack>
                    </Box>
                ) : (
                    <Button startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />} onClick={startAdd} sx={{ ...tonalBtn, height: 36, fontSize: 12.5, mb: 2 }}>
                        Log a communication
                    </Button>
                )}

                {list.length === 0 ? (
                    <EmptyState icon={ChatBubbleOutlineRoundedIcon} title="No communications logged yet" hint="Calls, emails, WhatsApp and meetings you log will show up here." />
                ) : (
                    <Stack spacing={1}>
                        {list.map((entry) => (
                            <Box key={entry.id} sx={{ p: 1.3, borderRadius: '9px', border: '1px solid #F3F8FC', opacity: busyId === entry.id ? 0.5 : 1 }}>
                                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                    <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                        <Chip label={entry.communicationType} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#E4F0F9', color: '#0E4A75' }} />
                                        <Chip label={entry.status} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1F5F9', color: '#475569' }} />
                                    </Stack>
                                    <Stack direction="row" spacing={0.2}>
                                        <IconButton size="small" disabled={Boolean(busyId)} onClick={() => startEdit(entry)} sx={{ color: '#94A3B8', '&:hover': { color: '#135F93', bgcolor: '#E4F0F9' } }}>
                                            <EditRoundedIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                        <IconButton size="small" disabled={Boolean(busyId)} onClick={() => setDeleteTarget(entry)} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                            <DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                                {entry.description && <Typography sx={{ fontSize: 12.5, color: '#475569', mt: 0.6 }}>{entry.description}</Typography>}
                                <Typography sx={{ fontSize: 10.5, color: '#98A0AE', mt: 0.6 }}>
                                    {entry.communicatedOn}{entry.followUpDate ? ` · follow up ${entry.followUpDate}` : ''}{entry.loggedBy ? ` · logged by ${entry.loggedBy}` : ''}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={close} sx={{ ...ghostBtn, height: 38, px: 2 }}>Close</Button>
            </DialogActions>

            <ConfirmDialog
                open={Boolean(deleteTarget)}
                onClose={() => { if (!busyId) setDeleteTarget(null); }}
                onConfirm={doDelete}
                title="Remove this entry?"
                body="This removes it from the candidate's communication log."
                confirmLabel="Remove"
            />
        </Dialog>
    );
}
